import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Form, Input, Select, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, TagsOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const { Option } = Select;

const AddCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/pcategory`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(data);
    } catch {
      Swal.fire('Error', 'Failed to load categories.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openModal = (category = null) => {
    if (category) {
      setEditId(category._id);
      form.setFieldsValue({ title: category.title });
    } else {
      setEditId(null);
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = editId ? `${BASEURL}/pcategory/${editId}` : `${BASEURL}/pcategory/createpcat`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: values.title }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', `Category ${editId ? 'updated' : 'added'}!`, 'success');
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Category?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${BASEURL}/pcategory/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Deleted!', 'Category removed.', 'success');
      fetchCategories();
    } catch (err) {
      Swal.fire('Error', err.message || 'Delete failed.', 'error');
    }
  };

  const filtered = categories.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));

  const columns = [
    {
      title: '#', key: 'idx', width: 60,
      render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span>,
    },
    {
      title: 'CATEGORY NAME', key: 'title',
      render: (r) => (
        <Space>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#e8f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2874f0' }}>
            <TagsOutlined />
          </div>
          <span style={{ fontWeight: 600, color: '#212121' }}>{r.title}</span>
        </Space>
      ),
    },
    {
      title: 'STATUS', key: 'status',
      render: () => <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 110,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Catalog</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Category List</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Category
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search category..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} categories` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{editId ? 'Edit Category' : 'Add Category'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Category Title"
            rules={[{ required: true, message: 'Title is required' }]}>
            <Input prefix={<TagsOutlined />} placeholder="Enter category name" size="large" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {editId ? 'Update' : 'Add Category'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddCategory;
